import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Images,
  Star,
  Wrench,
  BarChart3,
  Globe,
  Inbox,
  LogOut,
  ExternalLink,
  Building2,
  Package,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { BRAND } from "../lib/constants";

const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/gallery", label: "Gallery", icon: Images },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/clients", label: "Clients", icon: Building2 },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/services", label: "Services", icon: Wrench },
  { to: "/admin/stats", label: "Stats", icon: BarChart3 },
  { to: "/admin/content", label: "Site Content", icon: Globe },
  { to: "/admin/quotes", label: "Lead Inbox", icon: Inbox },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-layout">
      <aside className="fixed inset-y-0 left-0 w-64 bg-white border-r border-slate-200 hidden lg:flex flex-col">
        <div className="px-5 py-5 border-b border-slate-100 flex items-center gap-3">
          <img src={BRAND.logoMark} alt="Hayat" className="h-9 w-9 object-contain" />
          <div>
            <div className="font-display font-semibold text-sm text-slate-900">Hayat Admin</div>
            <div className="text-[11px] text-slate-500">{user?.email}</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-[#E8F4F8] text-[#1C6B8A]"
                      : "text-slate-700 hover:bg-slate-50"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-100 space-y-1">
          <Link
            to="/"
            target="_blank"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <ExternalLink className="w-4 h-4" /> View Site
          </Link>
          <button
            onClick={doLogout}
            data-testid="admin-logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <img src={BRAND.logoMark} alt="Hayat" className="h-7 w-7 object-contain" />
            <span className="font-display font-semibold text-sm">Hayat Admin</span>
          </div>
          <button
            onClick={doLogout}
            data-testid="admin-mobile-logout"
            className="text-xs font-semibold text-red-600"
          >
            Log out
          </button>
        </div>
        <nav className="flex gap-1 px-2 pb-2 overflow-x-auto no-scrollbar">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                  isActive ? "bg-[#1C6B8A] text-white" : "bg-slate-100 text-slate-700"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
      </div>

      <main className="lg:pl-64">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-6 md:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
